/**
 * regras — a parte pura da telemetria de erro e de rede.
 *
 * Sem DOM, sem Firebase, sem relógio próprio: tudo recebe o que precisa por
 * parâmetro. É o que permite testar com `node --test` (ver regras.test.js) e
 * é onde moram os números que decidem "instável", "já vi este erro" e "chega
 * de evento por esta sessão".
 */

/** Quatro falhas de transporte nesta janela, com o aparelho dizendo que está
 *  online, é o que chamamos de conexão instável.
 *
 *  Era 2 em 20 s e o modal aparecia com a rede boa: um long-poll do Firestore
 *  que o servidor encerra, uma imagem sem CORS, um fetch de terceiro que cai —
 *  qualquer dupla dessas parecia "instável". Quatro em 30 s, sem NENHUMA
 *  resposta no meio (uma resposta zera a conta), já é a rede falhando de
 *  verdade na frente da pessoa. */
export const JANELA_INSTAVEL_MS = 30_000;
export const FALHAS_PARA_INSTAVEL = 4;
/** Sem nenhuma falha por este tempo, "instável" volta a "estável". */
export const ACALMAR_MS = 30_000;

/** Tetos POR SESSÃO. `ANALYTICS_EVENTS` custa escrita; um `useEffect` em loop
 *  geraria milhares de eventos iguais em minutos. */
export const TETO_ERROS_SESSAO = 12;
export const TETO_REDE_SESSAO = 4;
export const TETO_EPISODIOS_SESSAO = 3;

/** Uma mensagem de erro cabe em 300; o resto é ruído repetido do bundle. */
export const MAX_MENSAGEM = 300;
export const MAX_PILHA = 400;

/**
 * Mantém só as falhas dentro da janela e diz se já são falhas o bastante.
 * @param {number[]} falhas timestamps (ms)
 * @param {number} agora
 * @return {{ recentes: number[], instavel: boolean }}
 */
export function avaliarFalhas(falhas, agora, {
  janela = JANELA_INSTAVEL_MS,
  minimo = FALHAS_PARA_INSTAVEL,
} = {}) {
  const recentes = falhas.filter((t) => agora - t <= janela);
  return { recentes, instavel: recentes.length >= minimo };
}

/**
 * Tipos efetivos que o Network Information API (Android/Chrome) reporta e
 * que já valem por "instável" antes de qualquer falha.
 *
 * Só `slow-2g`. O `effectiveType` é uma ESTIMATIVA de RTT do Chrome que cai
 * para "2g" em Wi-Fi bom com frequência (medida por amostras antigas) — era a
 * causa mais comum do modal aparecer com a conexão suficiente para usar o app.
 */
export const TIPOS_LENTOS = new Set(['slow-2g']);

export function conexaoLenta(effectiveType) {
  return TIPOS_LENTOS.has(String(effectiveType || '').toLowerCase());
}

/**
 * Mensagem que pode ir para uma coleção lida pelo painel inteiro.
 *
 * Corta no tamanho e mascara qualquer sequência longa de dígitos: uma mensagem
 * de validação pode carregar o CPF ou o telefone que a pessoa digitou, e o
 * evento de erro não precisa dele para ser útil.
 */
export function higienizar(texto, max = MAX_MENSAGEM) {
  const s = typeof texto === 'string' ? texto : String(texto ?? '');
  const mascarado = s.replace(/\d{6,}/g, '#');
  return mascarado.length > max ? `${mascarado.slice(0, max)}…` : mascarado;
}

/** Só as primeiras linhas da pilha: é onde está o arquivo e a linha. */
export function topoDaPilha(stack, linhas = 3, max = MAX_PILHA) {
  if (!stack || typeof stack !== 'string') return null;
  return higienizar(stack.split('\n').slice(0, linhas).join('\n'), max);
}

/** Chave de repetição: mesmo tipo e mesmo começo de mensagem = mesmo erro. */
export function chaveDoErro(tipo, mensagem) {
  return `${tipo || 'erro'}|${higienizar(mensagem, 120)}`;
}

/**
 * URLs que uma falha de `fetch` NÃO deve contar como rede: recursos locais e
 * o próprio esquema da WebView nunca dependem de internet.
 */
export function urlContaComoRede(url) {
  const s = String(url || '');
  return /^https?:\/\//i.test(s);
}

/** Falhas que não são de rede: cancelamento da própria página. */
export function erroEhCancelamento(err) {
  return err?.name === 'AbortError';
}
